import { cn } from '@forest/shared'
import {
  createEffect,
  For,
  onCleanup,
  onMount,
  untrack,
  type Accessor,
  type JSX,
} from 'solid-js'
import {
  createSlotMotion,
  REVEAL_SPARKLES,
  type SlotMotion,
} from '~/lib/refresh-reveal'
import { CARD_SURFACE_CLASS } from './card-surface'
import { QuestionMarkGlyph } from './icons/question-mark'

// What a grid tells its slots about the reveal (GLOSSARY.md "Refresh
// reveal"): the one face every card shares, whether the face-down cards are
// breathing, and the slot's own display position, which staggers its turn.
export interface SlotReveal {
  breathing: Accessor<boolean>
  displayIndex: number
  faceDown: Accessor<boolean>
}

/**
 * A Daily Picks slot's two faces: the card itself and the face-down "?"
 * card behind it, on a container that turns between them. Only the Daily
 * Picks grid mounts it — a Classic card never turns.
 *
 * The container's style always holds the current face, so a card that
 * mounts mid-reveal — the Featured tab left and returned to — arrives on
 * the right face without animating: the reveal is consumed, never replayed.
 * Only a change of face turns. The motion itself — the turn, the "?" face's
 * breath while the new assortment is awaited, the sparkle a new card lands
 * with — is the reveal module's; this component wires it to the DOM and the
 * slot's lifetime.
 */
export function CardFlip(props: { children: JSX.Element; reveal: SlotReveal }) {
  let card: HTMLDivElement | undefined
  let back: HTMLDivElement | undefined
  // Filled in render order, which is `REVEAL_SPARKLES` order.
  const sparkles: HTMLDivElement[] = []
  let motion: SlotMotion | undefined
  // The face at creation, read before any effect runs and deliberately not
  // tracked here: a card mounting into a face-down grid must not take the
  // flip up that follows as its own arrival.
  let face = untrack(() => props.reveal.faceDown())
  onMount(() => {
    if (!card || !back) return
    motion = createSlotMotion({ back, card, sparkles })
  })
  onCleanup(() => motion?.dispose())
  // Both effects run after the mount above, so they always find the motion.
  // The breath follows the flow's beat, at mount too: a card mounting into a
  // breathing grid — the Featured tab returned to mid-wait — breathes.
  createEffect(() => {
    motion?.breathe(props.reveal.breathing())
  })
  createEffect(() => {
    const faceDown = props.reveal.faceDown()
    if (faceDown === face) return
    face = faceDown
    motion?.turn(faceDown, props.reveal.displayIndex)
  })
  return (
    <div class="relative [perspective:1000px]">
      <div
        ref={(node) => {
          card = node
        }}
        data-testid="daily-pick-faces"
        data-face={props.reveal.faceDown() ? 'down' : 'up'}
        class="relative [transform-style:preserve-3d]"
        style={{
          transform: props.reveal.faceDown()
            ? 'rotateY(180deg)'
            : 'rotateY(0deg)',
        }}
      >
        <div class="[backface-visibility:hidden]">{props.children}</div>
        {/* The face-down "?" card, the design's unreveal slot (Figma node
            1655:27140): the slot's shared surface on the card's own footprint,
            a 2px white ring inset 7px, and the glyph centred. The pick behind
            it is hidden by its own backface, not by this face covering it.
            Stacked behind the card face, which decides nothing here — the two
            faces are sorted by their own rotation — but is the fallback that
            matters where a browser flattens the 3D context: the pick shows
            rather than a "?" card no tap can turn over. */}
        <div
          ref={(node) => {
            back = node
          }}
          aria-hidden="true"
          data-testid="daily-pick-face-down"
          class={cn(
            'absolute inset-0 -z-10 flex items-center justify-center [backface-visibility:hidden] [transform:rotateY(180deg)]',
            CARD_SURFACE_CLASS,
          )}
        >
          <QuestionMarkGlyph />
          <div class="absolute inset-[7px] rounded-[20px] border-2 border-white" />
        </div>
      </div>
      {/* The sparkle's particles, over both faces and outside the turn so
          they never rotate with the card: white, centred on the card, and
          invisible at rest — each burst starts and ends at opacity 0, so
          nothing here is ever shown or cleaned up between reveals. */}
      <div aria-hidden="true" class="pointer-events-none absolute inset-0">
        <For each={REVEAL_SPARKLES}>
          {(sparkle) => (
            <div
              ref={(node) => {
                sparkles.push(node)
              }}
              data-testid="daily-pick-sparkle"
              class={cn(
                'absolute left-1/2 top-1/2 bg-white opacity-0',
                sparkle.shape === 'circle' && 'rounded-full',
              )}
              style={{
                height: `${sparkle.size}px`,
                'margin-left': `${-sparkle.size / 2}px`,
                'margin-top': `${-sparkle.size / 2}px`,
                width: `${sparkle.size}px`,
              }}
            />
          )}
        </For>
      </div>
    </div>
  )
}
